import { createSelector, createSlice } from "@reduxjs/toolkit";
import {
  MAX_PINNED_PRIVATE_EFFICE_BATCH_SIZE,
  MAX_LATEST_EFFICE_BATCH_SIZE,
} from "constants/constants";
import axiosGeneral from "helpers/axios/axiosGeneral";
import { getCurrentToken } from "./authSlice";
import { rejoinEfficeChannels } from "slices/channels/personalChannelSlice";
import {
  cleanEffice,
  cleanEfficeList,
  isNewMemberEffice,
  isPrivateEffice,
  isRootEffice,
} from "helpers/efficeHelpers";
let dispatch = null;
let getState = null;
import("Store").then((module) => {
  dispatch = module.store.dispatch;
  getState = module.store.getState;
});

export const slice = createSlice({
  name: "efficeCache",
  initialState: {
    data: {},
    presenceState: {},
    fetchedLatest: {},
    fetchedPinned: {},
  },
  reducers: {
    setData: (state, action) => {
      state.data = action.payload;
    },
    dumpEfficeDescendants: (state, action) => {
      action.payload.forEach((pid) => {
        // Delete all descendants cache as it cannot be trusted anymore
        for (const i in state.data) {
          // Will not delete the head private effice as we still may need the state.data in UI
          if (
            state.data[i].permissions_id === pid &&
            state.data[i].id !== pid
          ) {
            delete state.data[i];
          }
        }
        // Delete records of what has been fetched
        for (const i in state.fetchedLatest) {
          if (state.fetchedLatest[i] === pid) {
            delete state.fetchedLatest[i];
          }
        }
        for (const i in state.fetchedPinned) {
          if (state.fetchedPinned[i] === pid) {
            delete state.fetchedPinned[i];
          }
        }
        // Delete Presense Data
        delete state.presenceState[pid];
      });
    },
    addSingleData: (state, action) => {
      let effice = action.payload;
      let { id, parent_id } = effice;

      // Updating Parent when backend does so as well

      if (
        state.data[parent_id] &&
        !state.data[id] &&
        !isRootEffice(effice) &&
        !isPrivateEffice(effice) &&
        !isNewMemberEffice(effice)
      )
        state.data[parent_id].sub_effices++;

      state.data[id] = effice;
    },
    addSingleDataNoParentUpdate: (state, action) => {
      state.data[action.payload.id] = action.payload;
    },
    uncountParentSubs: (state, action) => {
      state.data[action.payload].sub_effices--;
    },
    batchUpdateSubs: (state, action) => {
      let list = action.payload;
      list.forEach(({ id, subs }) => {
        if (state.data[id]) state.data[id].sub_effices = subs;
      });
    },
    addLocationData: (state, action) => {
      action.payload.forEach(({ node, role_id }) => {
        state.data[node.id] = state.data[node.id]
          ? { ...state.data[node.id], ...node, role_id: role_id }
          : { ...node, role_id: role_id };
      });
    },
    addBatchData: (state, action) => {
      action.payload.forEach((elem) => {
        state.data[elem.id] = state.data[elem.id]
          ? { ...state.data[elem.id], ...elem }
          : elem;
      });
    },
    addInitData: (state, action) => {
      action.payload.latest.forEach((elem) => {
        state.data[elem.id] = elem;
      });
      action.payload.pinned.forEach((elem) => {
        state.data[elem.id] = { ...state.data[elem.id], ...elem };
      });
      if (action.payload.efficeId && state.data[action.payload.efficeId]) {
        state.data[action.payload.efficeId] = {
          ...state.data[action.payload.efficeId],
          role_id: action.payload.roleId,
        };
      }
      state.fetchedLatest[action.payload.efficeId] =
        action.payload.permissionsId;
      state.fetchedPinned[action.payload.efficeId] =
        action.payload.permissionsId;
    },
    setFetchedLatest: (state, action) => {
      state.fetchedLatest = action.payload;
    },
    setFetchedPinned: (state, action) => {
      state.fetchedPinned = action.payload;
    },
    addFetchedLatest: (state, action) => {
      state.fetchedLatest[action.payload.id] = action.payload.permissions_id;
    },
    addFetchedPinned: (state, action) => {
      state.fetchedPinned[action.payload.id] = action.payload.permissions_id;
    },
    deleteFetchedLatest: (state, action) => {
      if (state.fetchedLatest[action.payload]) {
        delete state.fetchedLatest[action.payload];
      }
    },
    deleteFetchedPinned: (state, action) => {
      if (state.fetchedPinned[action.payload]) {
        delete state.fetchedPinned[action.payload];
      }
    },
    editEffice: (state, action) => {
      if (action.payload.id && state.data[action.payload.id]) {
        state.data[action.payload.id] = {
          ...state.data[action.payload.id],
          ...action.payload,
        };
      }
    },
    failEffice: (state, action) => {
      let nonce = action.payload;
      state.data[nonce].failed = true;
    },
    deleteEffice: (state, action) => {
      if (action.payload.id && state.data[action.payload.id]) {
        delete state.data[action.payload.id];
      }
    },
    setRoleId: (state, action) => {
      if (action.payload.effId && state.data[action.payload.effId]) {
        state.data[action.payload.effId] = {
          ...state.data[action.payload.effId],
          role_id: action.payload.role_id,
        };
      }
    },
    updatePresenceState: (state, action) => {
      let stateObject = action.payload;
      state.presenceState = { ...state.presenceState, ...stateObject };
    },
    updatePresenceDiffs: (state, action) => {
      let joins = action.payload.joins;
      let leaves = action.payload.leaves;
      for (const pid in state.presenceState) {
        if (joins[pid]) {
          for (const sid in joins[pid]) {
            if (state.presenceState[pid][sid]) {
              state.presenceState[pid][sid] = state.presenceState[pid][
                sid
              ].concat(joins[pid][sid]);
            } else {
              state.presenceState[pid][sid] = joins[pid][sid];
            }
          }
        }
        if (leaves[pid]) {
          for (const sid in leaves[pid]) {
            if (
              !state.presenceState[pid][sid] ||
              state.presenceState[pid][sid] <= 1
            ) {
              delete state.presenceState[pid][sid];
            } else {
              leaves[pid][sid].forEach((elem) => {
                state.presenceState[pid][sid] = state.presenceState[pid][
                  sid
                ].filter((existingElem) => {
                  return existingElem.user_id !== elem.user_id;
                });
              });
            }
          }
        }
      }
    },
  },
});

export const {
  setFetchedLatest,
  setFetchedPinned,
  addFetchedLatest,
  addFetchedPinned,
  addLocationData,
  addBatchData,
  addSingleData,
  addSingleDataNoParentUpdate,
  uncountParentSubs,
  batchUpdateSubs,
  addInitData,
  deleteFetchedLatest,
  deleteFetchedPinned,
  editEffice,
  deleteEffice,
  setRoleId,
  dumpEfficeDescendants,
  updatePresenceState,
  updatePresenceDiffs,
  failEffice,
} = slice.actions;

async function getLocationFromServer(nodeId) {
  let idToken = await getCurrentToken();

  let {
    data: { location_nodes },
  } = await axiosGeneral.get(`/location/${nodeId}`, {
    headers: {
      Authorization: "Bearer " + idToken,
    },
  });

  window.efs_debug &&
    window.log(
      "Get Location Response: " + JSON.stringify(location_nodes, null, 4)
    );
  let nodeFound = false;
  location_nodes.forEach((obj) => {
    obj.node = cleanEffice(obj.node);
    if (obj.node.id === nodeId) nodeFound = true;
  });
  if (nodeFound) window.efs_debug && window.log("Location Node found");
  else throw Error("Location Node Not Found In List Retrieved");
  dispatch(addLocationData(location_nodes));
}

const getLatestFromServer = (effId, l, before) =>
  new Promise((res, rej) => {
    getCurrentToken().then((idToken) => {
      axiosGeneral
        .get(
          `/effice/${effId}/latest?l=${l}${before ? `&before=${before}` : ``}`,
          {
            headers: {
              Authorization: "Bearer " + idToken,
            },
          }
        )
        .then(({ data: { effice_batch, role_id } }) => {
          window.efs_debug && window.log("Get Latest Response: ");
          window.efs_debug && window.log({ effice_batch, role_id });
          res({ effice_batch: cleanEfficeList(effice_batch), role_id });
        })
        .catch((error) => {
          window.efs_debug && window.log("Failed to Get Latest from Server");
          window.efs_debug && window.log(error);
          rej(error);
        });
    });
  });

const getPinnedFromServer = (effId, priv, afta) =>
  new Promise((res, rej) => {
    let route = `/effice/${effId}/`;
    if (afta) {
      route =
        route +
        "pinned_privates?priv=" +
        priv.toString() +
        "&afta=" +
        afta.toString();
    } else {
      route = route + "pinned_init?priv=" + priv.toString();
    }
    getCurrentToken().then((idToken) => {
      axiosGeneral
        .get(route, {
          headers: {
            Authorization: "Bearer " + idToken,
          },
        })
        .then(({ data: { effice_batch, role_id } }) => {
          window.efs_debug && window.log("Get Pinned Response: ");
          window.efs_debug && window.log({ effice_batch, role_id });
          res({ effice_batch: cleanEfficeList(effice_batch), role_id });
        })
        .catch((error) => {
          window.efs_debug && window.log("Failed to Get Pinned from Server");
          window.efs_debug && window.log(error);
          rej(error);
        });
    });
  });

const getInitFromServer = (effId, l, priv) =>
  new Promise((res, rej) => {
    getCurrentToken().then((idToken) => {
      axiosGeneral
        .get(`/effice/${effId}/init?l=${l}&priv=${priv}`, {
          headers: {
            Authorization: "Bearer " + idToken,
          },
        })
        .then(({ data: { latest, pinned, role_id } }) => {
          window.efs_debug && window.log("Get Init Response: ");
          window.efs_debug && window.log({ latest, pinned, role_id });
          res({
            latest: cleanEfficeList(latest),
            pinned: cleanEfficeList(pinned),
            role_id,
          });
        })
        .catch((error) => {
          window.efs_debug && window.log("Failed to Get Init from Server");
          window.efs_debug && window.log(error);
          rej(error);
        });
    });
  });

export async function getLocation(nodeId) {
  window.efs_debug &&
    window.log(
      "Get Location" + JSON.stringify(selectData(getState()), null, 4),
      2
    );
  let node = selectData(getState())[nodeId];
  if (node) {
    if (node.parent_id) {
      let preLocation = await getLocation(node.parent_id);
      preLocation.push(nodeId);
      return preLocation;
    } else return [nodeId];
  }
  await getLocationFromServer(nodeId);
  return getLocation(nodeId);
}

export const getLatest = (effId, before) => {
  return new Promise((res, rej) => {
    let data = selectData(getState());
    let effice = data[effId];
    let latest = [];
    let newBefore = before;
    let count = 0;
    let oldTime = Date.now();
    // debugger;
    if (before) {
      for (let id in data) {
        if (data[id].parent_id === effId && id < before && !data[id].pinned) {
          latest.push(id);
          count++;
          if (!newBefore) newBefore = id;
        }
      }
    } else {
      for (let i in data) {
        if (data[i].parent_id === effId && !data[i].pinned) {
          latest.push(i);
          count++;
          if (!newBefore) newBefore = i;
        }
      }
    }
    latest.sort((a, b) => b - a).splice(MAX_LATEST_EFFICE_BATCH_SIZE);
    if (
      count >= MAX_LATEST_EFFICE_BATCH_SIZE ||
      (count < MAX_LATEST_EFFICE_BATCH_SIZE &&
        getState().efficeCache.fetchedLatest[effId] &&
        !before)
    ) {
      dispatch(
        addFetchedLatest({ id: effId, permissions_id: effice.permissions_id })
      );
      res(latest);
    } else {
      let l = MAX_LATEST_EFFICE_BATCH_SIZE - count;
      getLatestFromServer(effId, l, newBefore)
        .then(({ effice_batch, role_id }) => {
          dispatch(addBatchData(effice_batch));
          dispatch(setRoleId({ effId, role_id }));
          dispatch(
            addFetchedLatest({
              id: effId,
              permissions_id: effice.permissions_id,
            })
          );
          res(latest.concat(effice_batch.map((eff) => eff.id)));
        })
        .catch(() => rej());
    }
    let newTime = Date.now();
    window.efs_debug &&
      window.log("Time to retrieve latest: " + (newTime - oldTime.toString()));
  });
};

export const getPinned = (effId) => {
  return new Promise((res, rej) => {
    let data = selectData(getState());
    let effice = data[effId];

    let pinned = [];
    let privatePinned = [];

    if (getState().efficeCache.fetchedPinned[effId]) {
      for (const i in data) {
        if (data[i].parent_id === effId && data[i].pinned) {
          if (data[i].permissions_id === i) privatePinned.push(i);
          pinned.push(i);
        }
      }
      pinned.sort((a, b) => a - b);

      dispatch(rejoinEfficeChannels(privatePinned));
      dispatch(
        addFetchedPinned({ id: effId, permissions_id: effice.permissions_id })
      );
      res(pinned);
    } else {
      getPinnedFromServer(effId, MAX_PINNED_PRIVATE_EFFICE_BATCH_SIZE)
        .then(({ effice_batch, role_id }) => {
          dispatch(addBatchData(effice_batch));
          dispatch(setRoleId({ effId, role_id }));
          dispatch(
            addFetchedPinned({
              id: effId,
              permissions_id: effice.permissions_id,
            })
          );
          res(pinned.concat(effice_batch.map((eff) => eff.id)));
        })
        .catch(rej);
    }
  });
};

export const getInit = (effId) => {
  return new Promise((res, rej) => {
    let data = { ...selectData(getState()) };

    let pinned = [];
    let latest = [];
    window.efs_debug && window.log("Get Init");

    if (
      getState().efficeCache.fetchedLatest[effId] &&
      getState().efficeCache.fetchedPinned[effId]
    ) {
      window.efs_debug && window.log("FetchedBoth");
      let toim = Date.now();
      let privatePinned = [];
      for (const i in data) {
        if (data[i].parent_id === effId) {
          if (data[i].pinned) {
            if (data[i].permissions_id === i) {
              privatePinned.push(data[i].permissions_id);
            }
            pinned.push(i);
          }
          if (!data[i].pinned) {
            latest.push(i);
          }
        }
      }
      pinned.sort((a, b) => a - b);
      latest.sort((a, b) => b - a).splice(MAX_LATEST_EFFICE_BATCH_SIZE);

      window.efs_debug &&
        window.log(
          "TOTAL TIME to FETCH FULLY CACHED INIT: " + (Date.now() - toim)
        );

      dispatch(rejoinEfficeChannels(privatePinned));
      res({ latest, pinned });
    } else if (getState().efficeCache.fetchedPinned[effId]) {
      window.efs_debug && window.log("FetchedPinned");

      let privatePinned = [];
      for (const i in data) {
        if (data[i].parent_id === effId && data[i].pinned) {
          if (data[i].permissions_id === i) {
            privatePinned.push(i);
          }
          pinned.push(i);
        }
      }
      pinned.sort((a, b) => a - b);
      dispatch(rejoinEfficeChannels(privatePinned));
      getLatest(effId, null)
        .then((latest) => res({ latest, pinned }))
        .catch((error) => {
          window.efs_debug && window.log("Failed Init - retrieving latest");
          window.efs_debug && window.log(error);
          rej(error);
        });
    } else if (getState().efficeCache.fetchedLatest[effId]) {
      window.efs_debug && window.log("FetchedLatest");

      for (const i in data) {
        if (data[i].parent_id === effId && !data[i].pinned) latest.push(i);
      }
      latest.sort((a, b) => b - a).splice(MAX_LATEST_EFFICE_BATCH_SIZE);
      getPinned(effId, null)
        .then((pinned) => res({ latest, pinned }))
        .catch((error) => {
          window.efs_debug && window.log("Failed Init - retrieving pinned");
          window.efs_debug && window.log(error);
          rej(error);
        });
    } else {
      window.efs_debug && window.log("Get new inits from server");

      getInitFromServer(
        effId,
        MAX_LATEST_EFFICE_BATCH_SIZE,
        MAX_PINNED_PRIVATE_EFFICE_BATCH_SIZE
      )
        .then(({ latest, pinned, role_id }) => {
          dispatch(
            addInitData({
              efficeId: effId,
              permissionsId: selectData(getState())[effId].permissions_id,
              roleId: role_id,
              latest: latest,
              pinned: pinned,
            })
          );
          res({
            latest: latest.map((eff) => eff.id),
            pinned: pinned.map((eff) => eff.id),
          });
        })
        .catch((error) => {
          window.efs_debug && window.log("Failed Init - retrieving both");
          window.efs_debug && window.log(error);
          rej(error);
        });
    }
  });
};

export const editRootEffice = (effice) => async (dispatch, getState) => {
  let effId = effice.id;
  let data = { ...selectData(getState()) };

  if (!data[effId]) {
    window.efs_debug &&
      window.log(
        "Client did not have the root effice in cache, it should if it was connected to the channel."
      );
    return;
  }

  dispatch(editEffice(effice));
};

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state) => state.counter.value)`
export const selectData = (state) => state.efficeCache.data;
export const makeSelectSingleData = () =>
  createSelector(
    (state) => state.efficeCache.data,
    (_, id) => id,
    (data, id) => data[id]
  );
export const selectPresenceState = (state) => state.efficeCache.presenceState;
export const makeSelectPpl = () =>
  createSelector(
    (state, _) => state.efficeCache.presenceState,
    (state, _) => state.efficeCache.data,
    (_, id) => id,
    (presenceState, data, id) => {
      if (
        data[id] &&
        presenceState[data[id].permissions_id] &&
        presenceState[data[id].permissions_id][id]
      ) {
        return presenceState[data[id].permissions_id][id].length;
      }
      return 0;
    }
  );

export default slice.reducer;
